import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useToaster } from "../../contexts/ToasterContext";
import integrations from "./data/integrations";
import { useIntegrationTour } from "../../components/Tour/IntegrationTour";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";

const IntegrationPage = () => {
  const { showToast } = useToaster();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrationsList, setIntegrationsList] = useState(integrations);

  // Initialize tour with auto-start for first-time users
  useIntegrationTour(true);

  useEffect(() => {
    // Initialize IAM provider availability
    updateIamProviderAvailability();
    
    // Listen for IAM provider status updates
    const handleIamStatusUpdate = (event) => {
      updateIamProviderAvailability(event.detail);
    };
    
    window.addEventListener('iamProviderStatusUpdate', handleIamStatusUpdate);
    
    // Simulate loading marketplace data
    const t = setTimeout(() => setLoading(false), 400);
    
    return () => {
      clearTimeout(t);
      window.removeEventListener('iamProviderStatusUpdate', handleIamStatusUpdate);
    };
  }, []);

  const updateIamProviderAvailability = (providerStatus = null) => {
    // Get current IAM provider status
    const currentStatus = providerStatus || {
      aws: true,
      gcp: true,
      azure: true
    };

    // Update integrations list with provider availability
    const updatedIntegrations = integrations.map(integration => {
      // Check if this is a cloud infrastructure integration that matches our providers
      if (integration.category === "Cloud Infrastructure" && 
          ['AWS', 'GCP', 'Azure'].includes(integration.label)) {
        const provider = integration.label.toLowerCase();
        return {
          ...integration,
          isAvailable: currentStatus[provider] || false
        };
      }
      return integration;
    });

    setIntegrationsList(updatedIntegrations);
  };

  const handleCardClick = (integration) => {
    // Only allow full flow for AWS, GitHub and S3
    const allowedLabels = ["AWS", "GitHub", "S3", "Active Directory"];

    if (!allowedLabels.includes(integration.label)) {
      showToast("Plan Upgrade Required", "info");
      return;
    }

    // Still block disabled providers (e.g. AWS not configured yet)
    if (integration.category === "Cloud Infrastructure" && 
        ["AWS", "GCP", "Azure"].includes(integration.label) && 
        !integration.isAvailable) {
      return;
    }

    if (integration.isAvailable && integration.href) {
      // Use React Router navigation for better SPA behavior
      navigate(integration.href);
    }
  };

  // Group integrations by category
  const categoryGroups = {
    "Cloud Infrastructure Analysis": ["Cloud Infrastructure"],
    "Cloud Storage & Buckets": ["Storage"],
    "Version Control System": ["Version Control System"],
    "Chat Applications": ["Messaging Services"],
    "Service Accounts": ["Service Accounts"],
    "DevOps Tools & CI/CD Pipelines": ["Tools & CI/CD Pipelines"],
    "Container Registries": ["Container Registries"],
    "Other SaaS Applications": ["SaaS Applications"],
  };

  const getIntegrationsByGroup = (categories) => {
    return integrationsList.filter((int) => categories.includes(int.category));
  };

  const ShimmerCard = () => (
    <div className="border border-gray-200 bg-gray-50 p-3 rounded-lg shadow-sm overflow-hidden w-24 h-28 flex-shrink-0">
      <div className="w-16 h-16 shimmer rounded-lg mx-auto mb-2"></div>
      <div className="w-16 h-3 shimmer rounded mx-auto pb-2"></div>
    </div>
  );

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Identity Management</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-black text-2xl md:text-3xl font-bold" data-tour="integration-title">
            Identity Management
          </h1>
          <p className="text-gray-600 mt-2" data-tour="integration-description">
            Connect your favourite cloud services and repositories to unlock
            powerful security monitoring and automation capabilities.
          </p>
        </div>

        {/* Marketplace-only content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6" data-tour="integration-categories">
          {Object.entries(categoryGroups).map(([groupName, categories]) => {
            const groupIntegrations = getIntegrationsByGroup(categories);
            
            // Determine tour attribute based on category name
            const getTourAttribute = (name) => {
              if (name === "Cloud Infrastructure Analysis") return "integration-cloud-infra";
              if (name === "Cloud Storage & Buckets") return "integration-storage";
              if (name === "Version Control System") return "integration-vcs";
              if (name === "Chat Applications") return "integration-communication";
              return null;
            };

            return (
              <div
                key={groupName}
                className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm"
                data-tour={getTourAttribute(groupName)}
              >
                <div className="flex flex-col items-start gap-8">
                  <h2 className="text-gray-800 text-xl font-semibold whitespace-nowrap flex-shrink-0">
                    {groupName}
                  </h2>
                  <div className="flex flex-wrap gap-3" data-tour={groupName === "Cloud Infrastructure Analysis" ? "integration-cards" : null}>
                    {loading
                      ? Array.from({ length: 4 }).map((_, i) => (
                          <ShimmerCard key={i} />
                        ))
                      : groupIntegrations.map((integration, idx) => {
                          const isCloudProvider = integration.category === "Cloud Infrastructure" && 
                                                ['AWS', 'GCP', 'Azure'].includes(integration.label);
                          const isDisabled = isCloudProvider && !integration.isAvailable;
                          const showPreviewLabel = ['AWS', 'S3', 'GitHub', 'Active Directory'].includes(integration.label);
                          
                          return (
                            <div
                              key={idx}
                              onClick={() => handleCardClick(integration)}
                              className={`border p-3 rounded-lg w-24 h-28 flex-shrink-0 relative
                                ${isDisabled 
                                  ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60' 
                                  : 'border-gray-200 bg-gray-50 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-[#C0ACF3] hover:bg-[#F4F0FD] group'
                                }`}
                              title={isDisabled ? "Configure scan in Manage Scans to enable" : integration.description}
                            >
                              {showPreviewLabel && (
                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
                                  <span className="bg-[#441a9a] text-white text-[9px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                                    Preview Available
                                  </span>
                                </div>
                              )}
                              <div className="flex flex-col items-center justify-center h-full">
                                <div className={`pt-2 flex items-center justify-center mb-2
                                  ${!isDisabled ? 'transition-transform group-hover:scale-110' : ''}`}
                                >
                                  <img
                                    src={integration.icon}
                                    alt={integration.label}
                                    className={`w-16 h-16 object-contain ${isDisabled ? 'grayscale' : ''}`}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </div>
                                <span className={`text-xs font-medium text-center leading-tight pb-2
                                  ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}
                                >
                                  {integration.label}
                                </span>
                                {isDisabled && (
                                  <div className="absolute top-1 right-1 w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs font-bold">!</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Shimmer animation
const style = document.createElement("style");
style.innerHTML = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.shimmer {
    background: linear-gradient(90deg, #f4f4f4 25%, #e0e0e0 50%, #f4f4f4 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
}
`;
document.head.appendChild(style);

export default IntegrationPage;
